import T from 'ramda/es/T';
import compose from 'ramda/es/compose';
import cond from 'ramda/es/cond';
import juxt from 'ramda/es/juxt';
import mergeDeepLeft from 'ramda/es/mergeDeepLeft';
import flip from 'ramda/es/flip';
import nthArg from 'ramda/es/nthArg';
import rejectNil from 'ramda-extension/es/rejectNil';
import isObject from 'ramda-extension/es/isObject';
import mergeDeepAllWith from 'ramda-extension/es/mergeDeepAllWith';
import headArg from 'ramda-extension/es/headArg';
import anyPass from 'ramda/es/anyPass';
import allPass from 'ramda/es/allPass';
import isNil from 'ramda/es/isNil';
import isArray from 'ramda-extension/es/isArray';
import all from 'ramda/es/all';
import evolve from 'ramda/es/evolve';
import ifElse from 'ramda/es/ifElse';
import isEmpty from 'ramda/es/isEmpty';
import keys from 'ramda/es/keys';
import map from 'ramda/es/map';
import merge from 'ramda/es/merge';
import pick from 'ramda/es/pick';
import pickBy from 'ramda/es/pickBy';
import when from 'ramda/es/when';
import values from 'ramda/es/values';
import o from 'ramda/es/o';
import alwaysNull from 'ramda-extension/es/alwaysNull';
import notNil from 'ramda-extension/es/notNil';
import notEmpty from 'ramda-extension/es/notEmpty';
import dispatch from 'ramda-extension/es/dispatch';
import isNilOrEmpty from 'ramda-extension/es/isNilOrEmpty';
import useWith from 'ramda/es/useWith';
import defaultToEmptyArray from 'ramda-extension/es/defaultToEmptyArray';
import always from 'ramda/es/always';

var allIsNil = all(isNil);

var isNilOrAllIsNil = anyPass([isNil, allPass([isArray, allIsNil])]);

/**
 * For merging validation results.
 * We don't want to null results replacing those already filled.
 */

var mergeResults = mergeDeepAllWith(cond([[isNilOrAllIsNil, nthArg(1)], [flip(isNilOrAllIsNil), headArg], [function (a, b) {
  return isObject(a) && isObject(b);
}, mergeDeepLeft], [T, headArg]]));
/**
 * Combine multiple validate schemes into one. If multiple schemes contains same validation, then the error validation * always wins.
 *
 * @param  {...Function} fns Validation functions.
 * @param {any} values
 * @return {Object} Merged result result.
 *
 * @alias module:core.combineValidate
 */

var combineValidate = function combineValidate() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return compose(mergeResults, rejectNil, juxt(fns));
};

var dispatchValidPredicates = function dispatchValidPredicates(predicates) {
  return function () {
    return isNilOrEmpty(predicates) ? null : dispatch(predicates).apply(void 0, arguments);
  };
};

var validObject = anyPass([isEmpty, o(function (x) {
  return !x;
}, values)]); // TODO: refactor

var validateObjectDescriptor = function validateObjectDescriptor(descriptor) {
  var evolution = map(dispatchValidPredicates)(descriptor);
  var keysToValidate = keys(descriptor);
  var dull = map(alwaysNull)(descriptor);
  return compose(when(validObject, alwaysNull), pickBy(allPass([notNil, notEmpty])), evolve(evolution), pick(keysToValidate), merge(dull));
};

var validateListDescriptor = function validateListDescriptor(listDescriptor) {
  return dispatchValidPredicates(listDescriptor);
};
/**
 * Applies validations in `descriptor` for `value`.
 * Params are curried.
 *
 * @param {object} descriptor Object that contains validations for each item in `values`
 * @param {any} value Value for validation
 * @alias module:core.validate
 *
 * @sig Object -> a -> b
 *
 * @example
 *
 * validate({
 * 	id: [(x) => !x && 'Is required.', (x) => x < 0 && 'Must be greater than 0.'],
 * 	name: [(x) => !x && 'Is required.'],
 * 	surname: [(x) => !x && 'Is required.'],
 * }, {
 * 		id: -1,
 * 		surname: 'Doe',
 * 	}
 * ])
 * // Output:
 * // 	{
 * // 		id: 'Must be greater than 0.',
 * // 		name: 'Is required.',
 * // 		surname: false,
 * // 	}
 *
 */


var validate = ifElse(isArray, validateListDescriptor, validateObjectDescriptor);

/**
 * Applies validations in `descriptor` for each item in `values`.
 * Params are curried.
 *
 * @param {object} descriptor Object that contains validations for each item in `values`
 * @param {array} values Values for validations
 * @sig Object -> [Object] -> [Object]
 * @alias module:core.validateMany
 *
 * @example
 *
 * validateMany({
 * 	id: [(x) => !x && 'Is required.', (x) => x < 0 && 'Must be greater than 0.'],
 * 	name: [(x) => !x && 'Is required.'],
 * 	surname: [(x) => !x && 'Is required.'],
 * }, [
 * 	{
 * 		id: -1,
 * 		surname: 'Doe',
 * 	},
 * 	{
 * 		id: 13,
 * 		name: 'Bob',
 * 	},
 * ])
 * // Output:
 * // [
 * // 	{
 * // 		id: 'Must be greater than 0.',
 * // 		name: 'Is required.',
 * // 		surname: false,
 * // 	}, {
 * // 		id: false,
 * // 		name: false,
 * // 		surname: 'Is required.',
 * // 	},
 * // ]
 *
 */

var validateMany = useWith(map, [validate, defaultToEmptyArray]);

/**
 * Creates validation function with predicate and message.
 * Results of validation is ready for translation by react-intl.
 * Result is valid if nil or empty is passed. Use required validation if you want to ensure that field is required.
 *
 * @param {Function} fn validation predicate.
 * @param {String} react intl message (eg. { id: 1, defaultMessage: '' })
 * @param {Object} params for intl message (eg. { min: 1, max: 2 })
 * @return {Object|null} Message object when fails { message, messageValues } or null if pass
 * @example
    > const hasLength = length => createValidation(hasLength(length), m.hasLength, { length })
	> hasLength(6)('abcdef')
	  null
 *
 * @alias module:core.createValidation
 */

var createValidation = (function (predicate, message, messageValues) {
  return cond([[isNilOrEmpty, alwaysNull], // null-safe before predicate
  [predicate, alwaysNull], [T, always({
    message: message,
    messageValues: messageValues
  })]]);
});

export { combineValidate, createValidation, validate, validateMany };
